import {SubmitSuccessPopup} from './screen-config';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {SubmitDialogComponent} from '../../forms/submit-dialog/submit-dialog.component';
import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {JSONPath} from 'jsonpath-plus';
import {PopupConfig} from '@shared/components/custom-action-button/custom-action-button-model';
import {IamAuthUtils} from '@shared/utils/iam-auth-utils';
import {Formio, Utils as FormioUtils} from '@evi-ui/js';
import moment from 'moment';
import {AclUtils} from '../../../iam/iam_utils/acl-utils';
import {DynamicDialogConfig} from 'primeng/dynamicdialog/dynamicdialog-config';
import {
  ActionChain,
  ActionListener,
  ActionType,
  ApiConfig,
  FieldValues,
  ScriptExecutionConfig,
  SmartTableActionConfig
} from '@shared/components/screen-configuration/screen-configuration.model';
import {LodashTemplateEvaluator} from '@shared/components/smart-table/LodashTemplateEvaluator';
import {firstValueFrom} from 'rxjs';
import {FormIODataTypes} from "@shared/model/enum/global-enum";
import {AnalyticsConfig} from "@shared/components/embedded-report/embedded-report.model";
import {EventType} from "@shared/model/event.data";

@Injectable({
  providedIn: 'root'
})
export class FormRendererUtils {
  constructor(private readonly dialogService: DialogService) {
  }

  public showPopupWithPromise(popupConfig: PopupConfig, submissionData: any) {
    const config = {
      width: popupConfig.width,
      duplicate: true,
      closable: false,
      modal: true,
      showHeader: false,
      contentStyle: {
        padding: 0
      },
      baseZIndex: 10000,
      data: {
        formId: popupConfig.popupScreenId,
        title: popupConfig.headerMessage || 'Message',
        description: popupConfig.message || '',
        inputData: _.cloneDeep(popupConfig.hasOwnProperty("data") ? popupConfig['data'] : submissionData), // NOSONAR: structuredClone breaks Angular form state
        parentInputData: _.cloneDeep(popupConfig.parentInputData), // NOSONAR: structuredClone breaks Angular form state
        closeRefOnDestroy: popupConfig.closeRefOnDestroy
      }
    };
    _.set(config, 'contentStyle.overflow', 'visible'); //IFAS-940 global fix for all popups
    _.set(config, 'contentStyle.max-height', '80vh');

    config['duplicate'] = true; //this is not required for 16.1.2 version, only auto upgraded version need this

    if (popupConfig.contentStyle) {
      config.contentStyle = popupConfig.contentStyle;
    }
    const dialogRef: DynamicDialogRef = this.dialogService.open(
      SubmitDialogComponent,
      config
    );

    if (dialogRef) {
      const promise = firstValueFrom(dialogRef?.onClose);
      return promise.then((r) => {
        dialogRef.destroy();
        return r;
      });
    } else {
      console.log('showPopupWithPromise :: Rejected');
      return Promise.reject(new Error('Dialog reference is not available'));
    }
  }

  public showPopup(popupConfig: PopupConfig, submissionData: any): void {
    const dialogRef: DynamicDialogRef = this.dialogService.open(
      SubmitDialogComponent,
      {
        width: '45%',
        closable: false,
        modal: true,
        showHeader: false,
        contentStyle: {
          padding: 0
        },
        baseZIndex: 10000,
        data: {
          formId: popupConfig.popupScreenId,
          title: popupConfig.headerMessage || 'Message',
          description: popupConfig.message || '',
          inputData: _.cloneDeep(submissionData) // NOSONAR: structuredClone breaks Angular form state
        }
      }
    );

    if (dialogRef) {
      dialogRef?.onClose.subscribe((result: any) => {
        if (
          popupConfig.returnValueStorageAttribute &&
          Object.keys(result).length
        ) {
          const existingData = _.get(
            submissionData,
            popupConfig.returnValueStorageAttribute
          );
          if (existingData) {
            result = _.merge(existingData, result);
          }
          _.set(
            submissionData,
            popupConfig.returnValueStorageAttribute,
            result
          );
        }
      });
    }
  }

  public openPopup(popupConfig: SubmitSuccessPopup): void {
    const defaultConfig = {
      width: '45%',
      closable: false,
      modal: true,
      showHeader: false,
      contentStyle: {
        padding: 0
      },
      baseZIndex: 10000
    };

    this.openPopupWithUIConfig(popupConfig, defaultConfig).then((res) => res);
  }

  public openPopupWithUIConfig(
    popupConfig: SubmitSuccessPopup,
    config: DynamicDialogConfig,
    submissionData?: any
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      config.data = {
        formId: popupConfig.popupScreenId,
        title: popupConfig.headerMessage
          ? popupConfig.headerMessage
          : 'Message',
        description: popupConfig.message ? popupConfig.message : '',
        returnUrl: popupConfig.redirectUrlAfterSubmission,
        inputData: popupConfig.data,
        inAppNavigation: popupConfig.inAppNavigation
      };

      _.set(config, 'contentStyle.overflow', 'visible'); //IFAS-940 global fix for all popups

      //workflow popup issue, the dialogs are cached and should allow duplicate to avoid reusability, as its returning nul in case same popupComponent present.
      // https://github.com/primefaces/primeng/blob/3bb279a38564dbf4a4a51595e974749cbffebce0/src/app/components/dynamicdialog/dialogservice.ts#L14
      //https://github.com/primefaces/primeng/blob/3bb279a38564dbf4a4a51595e974749cbffebce0/src/app/components/dynamicdialog/dialogservice.ts#L87

      config['duplicate'] = true; //this is not required for 16.1.2 version, only auto upgraded version need this
      const dialogRef: DynamicDialogRef = this.dialogService.open(
        SubmitDialogComponent,
        config
      );

      dialogRef.onClose.subscribe((result: any) => {
        console.log('Popup Closed: ', result);
        if (
          submissionData &&
          popupConfig.returnValueStorageAttribute &&
          result &&
          Object.keys(result).length
        ) {
          const existingData = _.get(
            submissionData,
            popupConfig.returnValueStorageAttribute
          );
          if (existingData) {
            console.log('existing data and merging the result', existingData);
            result = _.merge(existingData, result);
          }
          _.set(
            submissionData.data,
            popupConfig.returnValueStorageAttribute,
            result
          );
          console.log('resolving the  result from dialog close', result);
          resolve(result);
        }
      });
    });
  }

  /**
   * This method will resolve the variables that are nested and repetitive. For example in below JSON,
   * {
   *   "salesOrder": {
   *     "orderNumber": "1235",
   *     "orderDetails": [
   *       "lineItem: {
   *         "productId": "1236",
   *         "description": "Product 1"
   *       },
   *       "lineItem: {
   *         "productId": "7890",
   *         "description": "Product 2"
   *       }
   *     ]
   *   }
   * }
   *
   * productIds can be retrieved as salesOrder.lineItem[*].productId
   * It uses jsonpath-plus library to resolve the JSON node.
   * Refer https://registry.npmjs.org/jsonpath-plus for details of the library.
   *
   * @param template Template that needs to be resolved
   * @param data Data that can be used to assign value to the resolved variables.
   */
  public static resolveRepeatingParameters(template, data) {
    const templateCopy = String(template);
    let returnValue = String(template);
    // @ts-ignore
    const match = templateCopy.match(/({{\s*(.*?)\s*}})/g); // NOSONAR
    if (match) {
      match.forEach((substitutionParameter) => {
        let x = substitutionParameter;
        x = x.slice(3, -2);
        x = x.substring(0, x.lastIndexOf('.') - 1);
        const jsonPath = x;
        const jsonValues = JSONPath({path: jsonPath, json: data});
        if (jsonValues?.length) {
          const split = substitutionParameter.split(x);
          const newValue = split[0] + jsonValues.toString() + split[1];
          let evaluatedValue;
          try {
            const safeExpressionRegex = /^[0-9+\-*/().,\s]+$/;

            if (safeExpressionRegex.test(newValue)) {
              evaluatedValue = new Function(`"use strict"; return (${newValue})`)();
            } else {
              evaluatedValue = newValue;
            }
          } catch (e) {
            console.warn('Expression evaluation failed:', newValue, e);
            evaluatedValue = newValue;
          }
          const splitTemplate = returnValue.split(substitutionParameter);
          returnValue =
            splitTemplate[0] +
            encodeURIComponent(evaluatedValue) +
            (splitTemplate.length > 1 ? splitTemplate[1] : '');
        }
      });
    }
    return returnValue;
  }

  // NOSONAR
  public static nullifyRecursively(obj) { // NOSONAR
    for (const k in obj) {
      if (obj[k] == null) continue;
      if (typeof obj[k] == 'object' && obj[k] !== null) {
        FormRendererUtils.nullifyRecursively(obj[k]);
      }
      if (typeof obj[k] == 'object' && Object.keys(obj[k]).length == 0) {
        obj[k] = null;
      }
      if (obj[k] != null && typeof obj[k] == 'object') {
        const isEmpty = Object.values(obj[k]).every(
          (x) => x === null || x === ''
        );
        if (isEmpty) {
          obj[k] = null;
        }
      }
      if (typeof obj[k] == 'string' && _.isEmpty(obj[k])) {
        obj[k] = null;
      }
    }
  }

  public static addAuthHeaderOnSelectComponents(components: any[]) {
    components.forEach((component) => {
      if (component.type == 'select') {
        if (!component.data) {
          component.data = {};
        }
        if (!component.data.headers) {
          component.data.headers = [];
        }
        component.data.headers.push({
          key: 'Authorization',
          value: 'Bearer ' + IamAuthUtils.getCurrentUserToken()
        });
      }
      if (component.components && FormioUtils.isLayoutComponent(component)) {
        FormRendererUtils.addAuthHeaderOnSelectComponents(component.components);
      } else if (component.type === 'columns' && component.columns) {
        FormRendererUtils.addAuthHeaderOnSelectComponents(component.columns);
      }
    });
  }

  /**
   * IFAS-wide default validation for "ID Number" fields against a sibling "ID Type" field
   * (NRIC / FIN / UEN), auto-applied to every form without needing per-form Formio JSON edits.
   *
   * Convention: a component is treated as an ID Number/ID Type pair when their keys share the
   * same prefix and end (case-insensitively) with "idNumber" / "idType" respectively - e.g.
   * "partyDetails.idNumber" pairs with "partyDetails.idType", and a root-level "idNumber" pairs
   * with a root-level "idType". Skipped when the ID Number component is disabled, or its
   * Custom CSS Class includes "input-as-label" (rendered as a read-only label).
   */
  public static applyIdNumberValidation(components: any[]) {
    const idTypeComponents: { prefix: string; component: any }[] = [];
    const idNumberComponents: { prefix: string; component: any }[] = [];

    FormRendererUtils.eachComponent(components, (component: any) => {
      if (!component?.key) {
        return;
      }
      const { prefix, suffix } = FormRendererUtils.splitKeyByLastSegment(component.key);
      if (suffix === 'idtype') {
        idTypeComponents.push({ prefix, component });
      } else if (suffix === 'idnumber') {
        idNumberComponents.push({ prefix, component });
      }
    });

    idNumberComponents.forEach(({ prefix, component }) => {
      if (FormRendererUtils.skipIdNumberValidation(component)) {
        return;
      }
      const idTypeMatch = idTypeComponents.find((c) => c.prefix === prefix);
      if (!idTypeMatch) {
        return;
      }
      component.validate = component.validate || {};
      component.validate.custom = FormRendererUtils.buildIdNumberValidationScript(idTypeMatch.component.key);
    });
  }

  private static splitKeyByLastSegment(key: string): { prefix: string; suffix: string } {
    const lastDot = key.lastIndexOf('.');
    const suffix = (lastDot === -1 ? key : key.substring(lastDot + 1)).toLowerCase();
    const prefix = lastDot === -1 ? '' : key.substring(0, lastDot + 1);
    return { prefix, suffix };
  }

  private static skipIdNumberValidation(component: any): boolean {
    if (component.disabled) {
      return true;
    }
    const customClasses = String(component.customClass || '')
      .split(/\s+/)
      .filter(Boolean)
      .map((c) => c.toLowerCase());
    return customClasses.includes('input-as-label');
  }

  private static buildIdNumberValidationScript(idTypeKey: string): string {
    return [
      'const nricPattern = /^[ST]\\d{7}[A-Z]$/;',
      'const finPattern = /^[FGM]\\d{7}[A-Z]$/;',
      'function isValidUen(value) {',
      '  if (/^\\d{8}[A-Z]$/.test(value)) {',
      '    return true;', // businesses registered pre-2009 keep their legacy ACRA number, no year embedded
      '  }',
      '  if (/^\\d{9}[A-Z]$/.test(value)) {',
      '    const year = parseInt(value.substring(0, 4), 10);', // local companies: YYYYnnnnnX, scheme only started 2009
      '    return year >= 2009;',
      '  }',
      '  return /^[TSR]\\d{2}[A-Z]{2}\\d{4}[A-Z]$/.test(value);',
      '}',
      `const idType = _.get(data, '${idTypeKey}');`,
      'if (!input) {',
      '  valid = true;',
      "} else if (idType === 'NRIC') {",
      "  valid = nricPattern.test(input) ? true : 'Enter a valid NRIC Number';",
      "} else if (idType === 'FIN') {",
      "  valid = finPattern.test(input) ? true : 'Enter a valid FIN Number';",
      "} else if (idType === 'UEN') {",
      "  valid = isValidUen(input) ? true : 'Enter a valid UEN Number';",
      '} else {',
      '  valid = true;',
      '}'
    ].join('\n');
  }

  public checkAclPermission(components: any[]) {
    components.forEach((component) => {
      if (component.aclResource) {

        const aclKey = component.aclResource;

        const hasAccess = AclUtils.hasAccess(aclKey);
        console.log(`${aclKey} => hasAccess ?? ${hasAccess} => `);
        if (!hasAccess) {
         this.hideButtons([component]);
         try {
           this.hideComponent(component);
         }catch (e) {
           console.warn('Error while hiding component for acl:', aclKey, e);
         }
        }
      }
      if (component.components && FormioUtils.isLayoutComponent(component)) {
        this.checkAclPermission(component.components);
      } else if (component.type === 'columns' && component.columns) {
        this.checkAclPermission(component.columns);
      }
    });
  }

  // NOSONAR
  public static getComponent(componentKey: string, components: any[]) { // NOSONAR
    let returnMe;
    for (const component of components) {
      if (component.key === componentKey) {
        returnMe = component;
      }
      if (component.components && FormioUtils.isLayoutComponent(component)) {
        if (component?.component?.key === componentKey) {
          returnMe = component;
        } else {
          returnMe = FormRendererUtils.getComponent(
            componentKey,
            component.components
          );
        }
      }
      if (component.type === 'columns' && component.columns) {
        if (component?.component?.key === componentKey) {
          returnMe = component;
        } else {
          returnMe = FormRendererUtils.getComponent(
            componentKey,
            component.columns
          );
        }
      }

      if (returnMe) {
        return returnMe;
      }
    }
  }

  public static eachComponent(components: any[], fn: any) {
    for (const component of components) {
      fn(component);
      if (component.components && FormioUtils.isLayoutComponent(component)) {
        FormRendererUtils.eachComponent(
          component.components,
          fn
        );
      }

      if (component.type === 'columns' && component.columns) {
        FormRendererUtils.eachComponent(
          component.columns,
          fn
        );
      }

    }
  }

  public static getValue(submissionData: any, componentKey: string, defaultValue = '') {
    return _.get(submissionData, componentKey, defaultValue);
  }

  public static getComponentByType(
    componentType: string,
    components: any[],
    returnValues: any[] = [],
    parentComponent: any = undefined
  ): any[] {
    for (const component of components) {
      if (component.type === componentType) {
        returnValues.push({
          component: component,
          parentComponent: parentComponent
        });
      }
      if (component.components && FormioUtils.isLayoutComponent(component)) {
        FormRendererUtils.getComponentByType(
          componentType,
          component.components,
          returnValues,
          component
        );
      } else if (component.type === 'columns' && component.columns) {
        FormRendererUtils.getComponentByType(
          componentType,
          component.columns,
          returnValues,
          component
        );
      }
    }
    return returnValues;
  }

  public static evalContext(additional) {
    const contextArray = Array.isArray(additional) ? additional : [additional];
    const contextObjects = contextArray.map((obj) => ({
      _,
      utils: FormioUtils,
      util: FormioUtils,
      moment,
      instance: this,
      self: this,
      ...obj
    }));

    return Object.assign({}, ...contextObjects);
  }

  public static evaluate(func, additional, returnAttribute, tokenize = true) {
    return FormioUtils.evaluate(
      func,
      FormRendererUtils.evalContext(additional),
      returnAttribute,
      tokenize
    );
  }

  /**
   * updates the smart table with the given value. Smart table will also updates the submission context, to ensure data is not lost in subsequent reloads.
   * @param formComponents
   * @param tableKey
   * @param value
   */
  public static setDataOnSmartTable(
    formComponents: any,
    tableKey: any,
    value: any
  ) {
    FormioUtils.findComponent(formComponents, tableKey, '', (component) => {
      console.log(component.key, ' ', component.type, value);
      component.setValue({data: value});
    });
  }

  /**
   * reloads the data from the context. avoid using this method as much as possible as smart table is not getting the live copy  of data (submission.data) at times.
   * Use setDataOnSmartTable where ever possible, as it just update the smart table with data being set on it
   * @param formComponents
   * @param tableKey
   */
  public reloadSmartTable(formComponents: any, tableKey: any) {
    FormioUtils.findComponent(formComponents, tableKey, '', (component) => {
      component.setValue({
        action: 'refresh'
      });
    });
  }

  public openConfirmationPopup(popupConfig: PopupConfig): any {
    const dialogRef: DynamicDialogRef = this.dialogService.open(
      SubmitDialogComponent,
      {
        width: '45%',
        closable: false,
        modal: true,
        showHeader: false,
        contentStyle: {
          padding: 0
        },
        baseZIndex: 10000,
        data: {
          formId: popupConfig.popupScreenId,
          title: popupConfig.headerMessage
            ? popupConfig.headerMessage
            : 'Message',
          description: popupConfig.message ? popupConfig.message : ''
        }
      }
    );
    return dialogRef;
  }

  public hideButtons(components: any[]) {
    for (const component of components) {
      if (component.components && FormioUtils.isLayoutComponent(component)) {
        this.hideButtons(component.components);
      } else if (component.type === 'columns' && component.columns) {
        this.hideButtons(component.columns);
      } else {
        const isSubmitButton =
          component.type === 'button' &&
          (component.action === 'submit' || !component.action);
        if (isSubmitButton) {
          component.hidden = true;
        }
        if (component.type === 'toggle-button' && !component?.customOptions.showInWorkflowItem) {
          component.conditional.customConditional = 'show=false';
          component.customConditional = 'show=false';
          component.hidden = true;
        }
      }
    }
  }

  needsInterpolation(possibleDynamicValue: string): boolean {
    if (possibleDynamicValue) {
      const indexOfVariableStart = possibleDynamicValue.indexOf('{{');
      return (
        indexOfVariableStart > -1 &&
        possibleDynamicValue.indexOf('}}') > indexOfVariableStart + 1
      );
    }
    return false;
  }

  public addActionListeners(
    formInstance: any,
    components: any[],
    actionListeners: ActionListener[],
    formSubmission
  ) {
    if (!actionListeners || _.isEmpty(actionListeners)) {
      return;
    }

    actionListeners.forEach((actionListener) => {
      if (actionListener.eventName === EventType.FORM_READY) {
        return;
      }
      const widgetToListen = actionListener.widgetToListen;
      const eventName = actionListener.eventName;
      const actionChain = actionListener.actionChain;

      components.forEach((component) => {
        if (widgetToListen.includes(component.key)) {
          component.on(eventName, (eventData) => {
            this.handleActions(
              formInstance,
              actionChain,
              component,
              eventName,
              formSubmission,
              components,
              eventData,
              undefined
            );
          });
        } else if (
          component.components &&
          FormioUtils.isLayoutComponent(component)
        ) {
          this.addActionListeners(
            formInstance,
            component.components,
            actionListeners,
            formSubmission
          );
        } else if (component.type === 'columns' && component.columns) {
          this.addActionListeners(
            formInstance,
            component.columns,
            actionListeners,
            formSubmission
          );
        }
      });
    });
  }

  private async handleActions(  // NOSONAR
    formInstance: any,
    actionChain: ActionChain[],
    component: any, // NOSONAR
    eventName: string, // NOSONAR
    formSubmission: any,
    components: any[],
    eventData, // NOSONAR
    formDataLoaded$ // NOSONAR
  ) {
    let previousActionOutput = _.cloneDeep(formSubmission.data); // NOSONAR: structuredClone breaks Angular form state
    let actionProcessorsResult = {};

    for (const action of actionChain) {
      const actionResponse = await this.handleAction(
        formInstance,
        action,
        previousActionOutput,
        formSubmission,
        components,
        eventData,
        formDataLoaded$
      );
      const mergedData = _.merge(previousActionOutput, actionResponse);
      previousActionOutput = mergedData;
      actionProcessorsResult = _.merge(actionProcessorsResult, actionResponse);
      formInstance['actionProcessorsResult'] = actionProcessorsResult;
    }
  }

  private async handleAction(
    formInstance: any,
    action: ActionChain,
    resultAccumulator,
    formSubmission,
    components: any[], // NOSONAR
    eventData,
    formDataLoaded$
  ) {
    switch (action.actionType) {
      case ActionType.CALL_API :
      {
        const apiConfig = _.clone(action.apiConfig);
        apiConfig.url = LodashTemplateEvaluator.interpolate(
          apiConfig.url,
          resultAccumulator
        );
        apiConfig.url = LodashTemplateEvaluator.interpolate(
          apiConfig.url,
          formInstance
        );
        return this.handleApiCall(apiConfig, formSubmission);
      }
      case ActionType.EXECUTE_SCRIPT:
        return this.executeScript(
          action.scriptExecutionConfig,
          resultAccumulator,
          formSubmission,
          formInstance,
          eventData,
          formDataLoaded$
        );
      case ActionType.SET_FIELD_VALUES:
        return this.populateFields(
          action.fieldValuesConfig,
          resultAccumulator,
          formInstance.components,
          formSubmission,
          formDataLoaded$
        );
      case ActionType.SHOW_POPUP:
        return this.displayPopup(
          action.popupConfig,
          resultAccumulator,
          formSubmission,
          eventData
        );
      case ActionType.REFRESH_SMART_TABLE:
        return this.refreshSmartTable(action.smartTableConfig, components);
    }
  }

  private async populateFields(
    fieldValuesConfig: FieldValues[],
    resultAccumulator,
    components: any[],
    formSubmission: any,
    formDataLoaded$: any
  ) {
    return new Promise<any>((resolve, reject) => {
      for (const fieldValueConfig of fieldValuesConfig) {
        const fieldName = fieldValueConfig.fieldName;
        const component = FormRendererUtils.getComponent(fieldName, components);
        const fieldValue = fieldValueConfig.fieldValue;

        let valueToSet = _.get(resultAccumulator, fieldValue, '');
         valueToSet ??= '';
        console.log(`Setting value for fields ${fieldName} with variable ${fieldValue} and value as ${valueToSet}`);
        if (component?.type == 'smarttable') {
          component.setValue(valueToSet);
        } else if (component) {
          component.setValue(valueToSet);
          component.redraw();
        } else if (fieldName === 'data' && component == null) {
          formDataLoaded$.next(valueToSet);
        } else {
          console.error(
            'No component found with name ',
            fieldName,
            '. Setting data.' + fieldName
          );
          _.set(formSubmission.data, fieldName, valueToSet);
        }
      }
      resolve({populateFields: 'executed'});
    });
  }

  private async executeScript(
    scriptExecutionConfig: ScriptExecutionConfig,
    resultAccumulator, // NOSONAR
    formSubmission,
    formInstance,
    eventData,
    formDataLoaded$ // NOSONAR
  ) {
    return new Promise<any>((resolve, reject) => {
      const scriptValue = LodashTemplateEvaluator.interpolate(
        scriptExecutionConfig?.scriptToExecute,
        formSubmission
      );
      const result = FormRendererUtils.evaluate(
        scriptValue,
        [
          {submission: formSubmission},
          {formInstance: formInstance},
          {eventData: eventData}
        ],
        'result',
        true
      );
      let resultVariable = scriptExecutionConfig?.resultVariable;
      if (resultVariable) {
        _.set(formSubmission, resultVariable, result);
      }
      console.log('executeScript Result: ', result);
      resolve(result);
    });
  }

  private async displayPopup(
    popupConfig: any,
    resultAccumulator,
    formSubmission,
    eventData
  ) {
    const inputData = _.clone(formSubmission);
    inputData.eventData = eventData;
    inputData.otherResults = resultAccumulator;
    return this.showPopupWithPromise(popupConfig, inputData);
  }

  private async handleApiCall(
    apiConfig: ApiConfig,
    formSubmission: any
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      Formio.fetch(apiConfig.url, {
        headers: {
          'content-type': 'application/json',
          Authorization: 'Bearer ' + IamAuthUtils.getCurrentUserToken()
        },
        method: apiConfig.methodName
      }).then(function (response) {
        response.json().then(function (result) {
          if (apiConfig.responseContextVariable) {
            _.set(formSubmission, apiConfig.responseContextVariable, result);
          }
          resolve(result);
        });
      });
    });
  }

  prefixKeys(contextPrefix: any, components: any) {
    if (Array.isArray(components)) {
      for (const component of components) {
        if (component.components && FormioUtils.isLayoutComponent(component)) {
          this.prefixKeys(contextPrefix, component.components);
        } else if (component.type === 'columns' && component.columns) {
          this.prefixKeys(contextPrefix, component.columns);
        } else {
          component.key = contextPrefix + '.' + component.key;
        }
      }
    } else {
      components.key = contextPrefix + '.' + components.key;
    }
  }

  private refreshSmartTable(
    smartTableConfig: SmartTableActionConfig,
    components: any[]
  ) {
    return new Promise((resolve, reject) => {
      this.reloadSmartTable(components, smartTableConfig.smartTableId);
      resolve("{'action': 'refreshed}");
    });
  }

  resolveDynamicLabels(components: any, formSubmission: any) {
    for (const component of components) {
      if (component.components && FormioUtils.isLayoutComponent(component)) {
        this.updateTitle(component, formSubmission);
        this.resolveDynamicLabels(component.components, formSubmission);
      } else if (component.type === 'columns' && component.columns) {
        this.updateTitle(component, formSubmission);
        this.resolveDynamicLabels(component.columns, formSubmission);
      } else {
        this.updateTitle(component, formSubmission);
      }
    }
  }

  resolveDynamicHtmlContent(components: any, formSubmission: any) {
    for (const component of components) {
      if (component.components && FormioUtils.isLayoutComponent(component)) {
        this.resolveDynamicHtmlContent(component.components, formSubmission);
      } else if (component.type === 'columns' && component.columns) {
        this.resolveDynamicHtmlContent(component.columns, formSubmission);
      } else if (component.type === 'htmlelement') {
        this.updateHtmlContent(component, formSubmission);
      }
    }
  }

  private updateTitle(component: any, formSubmission: any) {
    const hasLegendProperty = component.component?.hasOwnProperty('legend');
    const hasTitleProperty = component.component?.hasOwnProperty('title');
    const hasLabelProperty = component.component?.hasOwnProperty('label');

    if (
      hasLegendProperty &&
      this.needsInterpolation(component.component.legend)
    ) {
      let legend = component.component.legend;
      legend = LodashTemplateEvaluator.interpolate(legend, formSubmission);
      component.component.legend = legend;
      component.redraw();
    }
    if (
      hasTitleProperty &&
      this.needsInterpolation(component.component.title)
    ) {
      let title = component.component.title;
      title = LodashTemplateEvaluator.interpolate(title, formSubmission);
      component.component.title = title;
      component.redraw();
    }
    if (
      hasLabelProperty &&
      this.needsInterpolation(component.component.label)
    ) {
      let label = component.component.label;
      label = LodashTemplateEvaluator.interpolate(label, formSubmission);
      component.component.label = label;
      component.redraw();
    }
  }

  showNestedFormsDiff(nestedFormsKey: string[], formInstance: any) {
    const firstNestedForm = FormRendererUtils.getComponent(
      nestedFormsKey[0],
      formInstance.components
    );
    FormRendererUtils.getComponent(
      nestedFormsKey[1],
      formInstance.components
    );
    FormioUtils.eachComponent(
      firstNestedForm.components,
      (component) => {
        if (!FormioUtils.isLayoutComponent(component)) {
          const componentValue = _.get(formInstance, component.key);
          console.log('****', component.key, component.type, componentValue);
        }
      },
      true
    );
  }

  private updateHtmlContent(component: any, formSubmission: any) {
    console.log(component, formSubmission);
    if (component.component?.attrs) {
      for (const attr of component.component.attrs) {
        if (this.needsInterpolation(attr.value)) {
          const rawValue = attr.value;
          attr.value = LodashTemplateEvaluator.interpolate(rawValue, formSubmission);
        }
      }
      component.redraw();
    }
    if (component?.component?.content) {
      const rawValue = component.component.content;
      component.component.content = LodashTemplateEvaluator.interpolate(rawValue, formSubmission);
    }
  }

  public interpolate(source: string, dataSource: any) {
    const str = source;
    let returnValue = "";
    if (source.includes("[") && source.includes("]")) {
      str.split("/").forEach(result => {
        if (result.includes("{{") && result.includes("}}")) {
          let parameterizedPart = result;
          parameterizedPart = parameterizedPart.replace("{{", "");
          parameterizedPart = parameterizedPart.replace("}}", "");
          returnValue = returnValue + "/" + _.get(dataSource, parameterizedPart, result);
        } else {
          returnValue = returnValue + "/" + result;
        }
      });
    } else {
      returnValue = LodashTemplateEvaluator.interpolate(
        str,
        dataSource
      )
    }

    return returnValue.replaceAll(/^\/+/g, '');
  }

  // NOSONAR
  resolveDataGridComponentAttributes(components, formData) { // NOSONAR
    for (const component of components) {
      if (component.components && FormioUtils.isLayoutComponent(component)) {
        this.resolveDataGridComponentAttributes(component.components, formData);
      } else if (component.type === 'columns' && component.columns) {
        this.resolveDataGridComponentAttributes(component.columns, formData);
      } else if (component.type == 'embedded-report') {
        if (component.parent?.type == FormIODataTypes.DATA_GRID) {
          const rowIndex = component.rowIndex;
          console.log(component.key, component.customOptions?.analyticsConfig);
          const analyticsConfig: AnalyticsConfig = component.component.customOptions.analyticsConfig;
          if (analyticsConfig?.embeddedDashboardId
            && this.needsInterpolation(analyticsConfig.embeddedDashboardId)) {
            analyticsConfig.embeddedDashboardId = analyticsConfig.embeddedDashboardId.replaceAll("rowIndex", rowIndex);
            analyticsConfig.embeddedDashboardId = this.interpolate(analyticsConfig.embeddedDashboardId, formData);
          }
        }
      } else {
        console.log(component);
      }
    }
  }

  public cleanupRequestData(submissionData: any, formInstance: any) : any {
    let dataCopy = _.cloneDeep(submissionData); // NOSONAR: structuredClone breaks Angular form state
    for (const component of formInstance.components) {
      if (component.components && FormioUtils.isLayoutComponent(component)) {
        dataCopy = this.cleanupRequestData(dataCopy, component);
      } else if (component.type === 'columns' && component.columns) {
        dataCopy = this.cleanupRequestData(dataCopy, component);
      } else if ((component.persistent && component.persistent !== true) || component.type == 'screen-configuration'
        || (component.component?.persistent !== true)) {
          delete dataCopy[component.key];
        }
    }
    return dataCopy;
  }

  private hideComponent(component: any) {
   if(component) {
     component.hidden = true;
     console.log("Hiding Component : ", component.label)}
  }
}
